/**
 * Flags the CMS checks. Mirrors `FeatureFlagKey` in backend-booking
 * (`src/resource/feature-flag/feature-flag.constants.ts`); add a key there
 * first, then here.
 *
 * Two kinds, told apart by prefix — the mechanism is the same:
 *  - `release.<module>.<feature>` rolls a change out client by client and is
 *    removed from both registries once every client has it on.
 *  - `module.<module>` switches an existing module off per client and stays
 *    for good; never clean it up with the release flags.
 *
 * A const object rather than an `enum`, so the registry can be empty and still
 * type as strings.
 *
 * @example
 * export const FeatureFlagKey = {
 *   BOOKING_MODULAR_FLOW: 'release.booking.modular-flow',
 *   GIFT_CARDS: 'module.gift-cards',
 * } as const satisfies Record<string, FeatureFlagKeyFormat>;
 */
type FeatureFlagKeyFormat = `release.${string}` | `module.${string}`;

export const FeatureFlagKey = {
  // Staged releases.
  // Availability, its rules/calendar/temporary capacity endpoints and the
  // Planner's hours; off keeps Opening Hours & Dates and the closure banner.
  RELEASE_SCHEDULE_AVAILABILITY: 'release.schedule.availability',
  // Smart Pricing V2, on the Availability design (ELI-419), beside the old
  // Smart Pricing page. Same /smart-pricing/* endpoints, so the API has no
  // guard on it; off only hides the page.
  RELEASE_PRICING_SMART_PRICING: 'release.pricing.smart-pricing',
  // Permanent module switches.
  MODULE_TODAY: 'module.today',
  MODULE_DASHBOARD: 'module.dashboard',
  MODULE_PROMO_BANNER: 'module.promo-banner',
  MODULE_PAYMENT_OVERVIEW: 'module.payment-overview',
  // Only managing these is switched off: their existing data still feeds the
  // booking form, the widget and pricing.
  MODULE_COUPONS: 'module.coupons',
  MODULE_GIFT_CARDS: 'module.gift-cards',
  MODULE_SMART_PRICING: 'module.smart-pricing',
  MODULE_SMART_SUGGESTION: 'module.smart-suggestion',
  MODULE_ADD_ONS: 'module.add-ons',
  MODULE_FOOD_AND_BEVERAGE: 'module.food-and-beverage',
  MODULE_RESELLERS: 'module.resellers',
  MODULE_GROUP_TYPES: 'module.group-types',
} as const satisfies Record<string, FeatureFlagKeyFormat>;

export type FeatureFlagKey =
  (typeof FeatureFlagKey)[keyof typeof FeatureFlagKey];

/**
 * The 404 message the API answers with when a flagged endpoint is off for the
 * company. Kept in sync with `FEATURE_UNAVAILABLE_MESSAGE` in the backend.
 */
export const FEATURE_UNAVAILABLE_MESSAGE =
  'This feature is not available for this account';
